import { AuthLayout } from '@/modules/auth/ui/components'
import { LoadingSpinner } from '@/shared/ui/components'
import { Button } from '@/shared/ui/primitives'
import type { ExchangeImpersonationHandoff, LoadSession, SetSession } from '@/modules/auth/use-cases'
import type { LoadMemberContext } from '@/shared/domain'
import { useImpersonationHandoffPage } from './hooks/use-impersonation-handoff-page'

export type ImpersonationHandoffPageProps = {
  onExchangeImpersonationHandoff: ExchangeImpersonationHandoff
  onSetSession: SetSession
  onLoadSession: LoadSession
  onLoadMemberContext: LoadMemberContext
}

export function ImpersonationHandoffPage(props: ImpersonationHandoffPageProps) {
  const vm = useImpersonationHandoffPage(props)

  if (vm.status === 'expired') {
    return (
      <AuthLayout
        title="Support session expired or invalid"
        subtitle="This impersonation link is no longer valid."
      >
        <div data-testid="impersonation-expired" className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            The impersonation session has expired or the link was already used. Reopen the
            organization from the admin dashboard to start a new support session.
          </p>
        </div>
      </AuthLayout>
    )
  }

  if (vm.status === 'failed') {
    return (
      <AuthLayout title="Could not open organization" subtitle="Something went wrong.">
        <div data-testid="impersonation-failed" className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            We couldn't complete the support session. Reopen the organization from the admin
            dashboard to try again.
          </p>
          <Button onClick={vm.onReturnToDashboard} variant="default" className="w-full">
            Go to Command Center
          </Button>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title="Opening organization" subtitle="Hang tight while we open the support session.">
      <div
        data-testid="impersonation-loading"
        className="flex flex-col items-center justify-center gap-3 py-6"
      >
        <LoadingSpinner size="lg" />
        <p className="text-sm text-muted-foreground">{vm.message}</p>
      </div>
    </AuthLayout>
  )
}
